import { QueryClient, QueryKey } from '@tanstack/react-query';

import { logger } from '@/utils/logger/logger';

import { ACTIVE_TIMER_QUERY_KEYS } from '../constants/active-timer-query-keys';
import { APPROVALS_QUERY_KEYS } from '../constants/approvals-query-keys';
import { TIME_TRACKING_QUERY_KEYS } from '../constants/time-tracking-query-keys';

const TAG = 'TimeTrackingInvalidation:';

/**
 * Invalidates query families so no fetch started BEFORE the caller's mutation can
 * win the race.
 *
 * `invalidateQueries` alone is not enough: `Query.fetch` only discards an in-flight
 * fetch when the query already holds data, so a query on its first load is handed
 * back the pre-mutation response and marks it fresh. `revert: false` because we
 * replace the cancelled fetch immediately — reverting would only flicker the UI.
 */
const cancelThenInvalidate = async (
  queryClient: QueryClient,
  queryKeys: readonly QueryKey[]
): Promise<void> => {
  await Promise.all(
    queryKeys.map(queryKey => queryClient.cancelQueries({ queryKey }, { revert: false }))
  );

  await Promise.all(queryKeys.map(queryKey => queryClient.invalidateQueries({ queryKey })));

  // `invalidateQueries` reports success even when it refetched nothing (it swallows
  // refetch errors and skips paused fetches) — that silence is how "I saved and the
  // list never changed" reaches a user alongside a success toast. Active queries
  // only: inactive ones are meant to stay flagged until their next mount.
  const stillInvalidated = queryKeys.filter(queryKey =>
    queryClient
      .getQueryCache()
      .findAll({ queryKey, type: 'active' })
      .some(query => query.state.isInvalidated)
  );
  if (stillInvalidated.length > 0) {
    logger.warn(TAG, 'Some queries are still stale after invalidation', {
      queryKeys: stillInvalidated,
    });
  }
};

export const invalidateActiveTimerQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: ACTIVE_TIMER_QUERY_KEYS.all });
};

/**
 * Every query family that renders worklog data for the current user: the tracked-time
 * lists and the day/week/month overviews that aggregate them.
 */
const TIME_TRACKING_VIEW_QUERY_KEYS: readonly QueryKey[] = [
  TIME_TRACKING_QUERY_KEYS.timeTracksAll,
  TIME_TRACKING_QUERY_KEYS.summaryAll,
  TIME_TRACKING_QUERY_KEYS.weekOverviewAll,
  TIME_TRACKING_QUERY_KEYS.monthOverviewAll,
  TIME_TRACKING_QUERY_KEYS.dayOverviewAll,
];

/**
 * Refreshes everything that reflects an approval change EXCEPT the heavy approvals worklog
 * sections (those are patched in place by `patchApprovalWorklogs` + selectively re-fetched there
 * for fresh hashes — refetching every loaded worklog page is what made approve/unapprove laggy).
 *
 * That means: the approval aggregates (summary tiles, project + member totals) AND the regular
 * time-tracking views (tracked-time lists and day/week/month overviews), which render per-worklog
 * approval state (`track.isApproved`) and would otherwise show stale data after approving here.
 */
export const invalidateApprovalAggregates = async (queryClient: QueryClient): Promise<void> => {
  await cancelThenInvalidate(queryClient, [
    APPROVALS_QUERY_KEYS.summaryAll,
    APPROVALS_QUERY_KEYS.projectsAll,
    APPROVALS_QUERY_KEYS.membersAll,
    ...TIME_TRACKING_VIEW_QUERY_KEYS,
  ]);
};

/**
 * Invalidates all time tracking and time tracking approvals queries.
 * Use when project or team member data changes in a way that may affect
 * the tracked time lists, summaries, or approval screens (e.g. project archived,
 * team member deleted, rates updated).
 */
export const invalidateTimeTrackingQueries = async (queryClient: QueryClient): Promise<void> => {
  await cancelThenInvalidate(queryClient, [
    ...TIME_TRACKING_VIEW_QUERY_KEYS,
    APPROVALS_QUERY_KEYS.all,
  ]);
};
